<template>
  <LayoutPage :title="pageTitle" :loading="devicesLoading">
    <form @submit.prevent="addSmartUnit">
      <v-row>
        <v-col lg="6">
          <CardLayout :title="cardTitle">
            <v-alert
              v-model="unitExistsAlert"
              dark
              color="indigo darken-3"
              transition="scale-transition"
            >
              <h3>
                Smart {{ capitalizedSubtype }} "{{ smartUnit.name }}" already
                exists
              </h3>

              <p class="subtitle-1">Would you like to...</p>

              <ul style="list-style: none; padding: 0px">
                <li v-if="renterId">
                  <v-btn text @click="selectSmartUnit">
                    <v-icon left small>fas fa-chevron-circle-right</v-icon>
                    Select Smart Unit "{{ smartUnit.name }}" to Assign to
                    {{ renterName }}
                  </v-btn>
                </li>
                <li v-else>
                  <v-btn text @click="selectSmartUnit">
                    <v-icon left small>fas fa-chevron-circle-right</v-icon>
                    Select Smart {{ capitalizedSubtype }} "{{ smartUnit.name }}"
                  </v-btn>
                </li>
                <li>
                  <v-btn text @click="useDifferentName">
                    <v-icon left small>fas fa-chevron-circle-right</v-icon>
                    Try a Different {{ capitalizedSubtype }} #
                  </v-btn>
                </li>
              </ul>
            </v-alert>

            <v-autocomplete
              ref="unitName"
              v-model="smartUnit"
              :disabled="unitExistsAlert"
              :items="existingUnits"
              :loading="existingUnitsLoading"
              :search-input.sync="search"
              auto-select-first
              clearable
              clear-icon="far fa-times-circle"
              hide-no-data
              :item-disabled="(item) => item.nameTooShort"
              item-text="name"
              item-value="id"
              :label="labelName"
              no-filter
              return-object
              @change="unitSelected"
              @click:clear="onClearClicked"
            >
              <template #item="{ item, on, attrs }">
                <!-- Existing, assigned unit -->
                <template v-if="item.assigned">
                  <v-list-item
                    :to="{
                      name: 'ManageSmartUnitDevices',
                      params: { zone_id: item.id },
                    }"
                    v-bind="attrs"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="grey--text caption">
                        {{ assignedDevices(item) }}
                        <span v-if="item.renter" class="ml-2">
                          <strong>
                            {{ item.renter.firstName }}
                            {{ item.renter.lastName }}
                          </strong>
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-icon>
                      <v-icon color="grey lighten-1" small>
                        fas fa-user-shield
                      </v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                </template>

                <!-- Existing, unassigned unit -->
                <template v-else-if="item.id">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="caption">
                        {{ assignedDevices(item) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-icon title="Existing Smart Unit">
                      <v-icon color="" small> fas fa-check-circle </v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                </template>

                <!-- Nonexistent unit, ok to create -->
                <template v-else>
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle
                        v-if="item.nameTooShort"
                        class="caption font-italic"
                      >
                        Smart {{ subtype }} # must be at least
                        {{ unitNameMinLength }} characters
                      </v-list-item-subtitle>
                      <v-list-item-subtitle v-else class="caption font-italic">
                        Click to continue creating Smart {{ subtype }}
                        {{ item.name }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-icon title="Click to Create Smart Unit">
                      <v-icon
                        :color="item.nameTooShort ? 'grey' : 'success'"
                        small
                      >
                        fas fa-plus-circle
                      </v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                </template>
              </template>
            </v-autocomplete>
            <div v-if="subtype === 'zone'" class="caption grey--text mb-2">
              Alpha-numeric &middot; max 100 characters &middot; must be unique
              per facility
            </div>
            <!-- Time zone is no longer chosen here; a new zone inherits its
                 facility's time zone (set in the create payload below). -->
            <SelectUnitType
              v-if="subtype !== 'zone'"
              ref="unitType"
              v-model="smartUnit.unitTypeId"
              :disabled="
                !smartUnit.name || unitExistsAlert || existingUnitSelected
              "
            />

            <!-- Unit Mapping: assign unmapped facility units to the new zone -->
            <div v-if="subtype === 'zone'" class="mt-5">
              <div class="d-flex align-center mb-2">
                <span class="subtitle-2 font-weight-bold">Unit Mapping</span>
                <v-spacer />
                <span class="caption grey--text">
                  {{ selectedUnitIds.length }} of {{ unmappedUnits.length }}
                  unmapped selected
                </span>
              </div>
              <v-text-field
                v-model="unitSearch"
                placeholder="Search by unit #, type, size…"
                dense
                outlined
                clearable
                class="mb-2"
                prepend-inner-icon="mdi-magnify"
              />
              <div v-if="facilityUnitsLoading" class="text-center py-4">
                <v-progress-circular indeterminate color="primary" size="24" />
              </div>
              <div
                v-else-if="unmappedUnits.length === 0"
                class="grey--text caption py-2"
              >
                No unmapped units available in this facility.
              </div>
              <div v-else style="max-height: 320px; overflow-y: auto">
                <v-list dense>
                  <v-list-item
                    v-for="unit in filteredUnmappedUnits"
                    :key="unit.id"
                    class="unit-row px-2"
                  >
                    <v-list-item-action class="my-0 mr-3">
                      <v-checkbox
                        v-model="selectedUnitIds"
                        :value="unit.id"
                        dense
                        hide-details
                        class="mt-0 pt-0"
                      />
                    </v-list-item-action>
                    <span class="font-weight-bold unit-name">
                      {{ unit.name }}
                    </span>
                    <span
                      class="caption grey--text ml-4 unit-details text-truncate"
                    >
                      {{ unitDetails(unit) }}
                    </span>
                    <div class="device-cell">
                      <template v-if="smartDeviceIds(unit).length">
                        <v-chip
                          v-for="id in smartDeviceIds(unit)"
                          :key="id"
                          x-small
                          label
                          color="green lighten-5"
                          class="green--text text--darken-2 font-weight-medium"
                        >
                          ✓ {{ id }}
                        </v-chip>
                      </template>
                      <span v-else class="caption grey--text">— no device</span>
                    </div>
                  </v-list-item>
                </v-list>
              </div>
            </div>
          </CardLayout>
        </v-col>

        <v-col lg="6">
          <CardLayout
            v-model="deviceFilter"
            title="Select Devices"
            :loading="devicesLoading"
            :disabled="!smartUnit.name"
            header-type="search"
          >
            <template #load>
              <em>Loading available devices</em>
            </template>

            <!-- For zones, devices are split into Cameras / Sensors tables at
                 the UI level only; both feed the same selectedDeviceIds. -->
            <div v-for="group in deviceGroups" :key="group.key">
              <div v-if="group.label" class="overline grey--text mt-3 mb-1">
                {{ group.label }}
              </div>
              <div
                v-if="group.label && group.devices.length === 0"
                class="grey--text caption mb-2"
              >
                No {{ group.label.toLowerCase() }} available.
              </div>
              <v-simple-table v-else>
                <template #default>
                  <thead>
                    <tr>
                      <th>Assign Device #</th>
                      <th>Device Type</th>
                      <th v-if="subtype === 'zone'">Device Location</th>
                      <th>Device Health</th>
                      <th>Last Motion</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="device in group.devices" :key="device.id">
                      <td style="width: 40%">
                        <v-checkbox
                          v-model="selectedDeviceIds"
                          dense
                          :disabled="device.status !== 'ok'"
                          :label="device.name"
                          :value="device.id"
                        />

                        <v-badge
                          v-if="device.labels?.isRefresh"
                          color="orange"
                          content="Refresh"
                          class="ml-5 d-inline"
                        />
                      </td>
                      <td>{{ deviceType(device) }}</td>
                      <td v-if="subtype === 'zone'">
                        <CardDeviceLocations
                          v-model="device.specs"
                          :device="device"
                          :zone-id="selectedExistingZoneId"
                          :allow-update="existingUnitSelected"
                        />
                      </td>
                      <td style="width: 30%; white-space: nowrap">
                        <DeviceGaugeCluster :device="device" />
                      </td>
                      <td style="width: 30%; white-space: nowrap">
                        {{ device.lastMotion }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <template #actions>
              <v-btn outlined class="mr-3" @click="cancel">Cancel</v-btn>

              <template v-if="existingUnitSelected == false">
                <span
                  :title="
                    selectedDeviceIds.length === 0
                      ? 'Please select at least one unit for this zone.'
                      : ''
                  "
                  class="d-inline-block"
                  @click="markSubmitAttempted"
                >
                  <v-btn
                    :disabled="!allowCreation"
                    color="success"
                    type="submit"
                  >
                    Create Smart {{ capitalizedSubtype }}
                  </v-btn>
                </span>
              </template>
              <template v-else>
                <span
                  :title="
                    selectedDeviceIds.length === 0
                      ? 'A zone must have at least one unit.'
                      : ''
                  "
                  class="d-inline-block"
                  @click="markSubmitAttempted"
                >
                  <v-btn
                    :disabled="!allowCreation"
                    color="success"
                    type="submit"
                  >
                    Update Existing Smart {{ capitalizedSubtype }}
                  </v-btn>
                </span>
              </template>
              <div
                v-if="
                  submitAttempted &&
                  selectedDeviceIds.length === 0 &&
                  subtype !== 'zone'
                "
                class="error--text caption mt-1 ml-2"
              >
                <span v-if="existingUnitSelected">
                  A zone must have at least one unit.
                </span>
                <span v-else>
                  Please select at least one unit for this zone.
                </span>
              </div>
            </template>
          </CardLayout>
        </v-col>
      </v-row>
    </form>
    <DialogLayout
      v-model="openRefreshDeviceWarning"
      title="Device Refresh Alert"
    >
      <p>
        This device was for refresh purposes only. Assigning it to a new renter
        will incur an {{ deviceChargeAmount }}device charge for your facilities
        next billing cycle. Would you like to proceed with assigning this unit?
      </p>
      <template #actions>
        <v-btn color="primary" text @click="openRefreshDeviceWarning = false">
          No
        </v-btn>
        <v-btn
          color="primary"
          text
          @click="doAssignZoneToRenter(zoneWithRefreshWarning, redirectFn)"
        >
          Yes
        </v-btn>
      </template>
    </DialogLayout>
  </LayoutPage>
</template>

<script>
import LayoutPage from "@layout/LayoutPage.vue";
import CardLayout from "@layout/CardLayout.vue";
import DialogLayout from "@layout/DialogLayout.vue";

import { userDisplayName } from "../renters/helper";

import { assignZoneToRenter } from "../renters/renter";
import { addSmartUnit } from "../../interactors/smartUnit";

import { findUser, userBillingAccounts } from "../../graphql/users";

import { addOrganizationDevices } from "../../graphql/organizations";

import DeviceGaugeCluster from "@atoms/DeviceGaugeCluster.vue";
import SelectUnitType from "../common/SelectUnitType.vue";
import { deviceLastMotion } from "../common/helpers/devices";
import useFacility from "../authentication/useFacility";
import useInventory from "./useInventory";
import { computed, onUnmounted, ref, unref, watch } from "vue";
import { stringSearch, titleCase } from "@tod-ui/helpers/strings";
import useDevice from "./useDevice";
import useSmartUnits from "./useSmartUnits";
import usePMS from "../common/usePMS";
import useAlerts from "@tod-ui/composables/useAlerts";
import { useRoute } from "vue-router/composables";
// Time-zone selection removed from zone creation (zone inherits facility tz).
// import InputTimeZone from "@tod-ui/components/InputTimeZone.vue";
import CardDeviceLocations from "@components/smart_zones/CardDeviceLocations.vue";
import { deviceType } from "@tod-ui/helpers/devices";
import {
  GET_FACILITY_UNITS,
  GET_FACILITY_CAMERAS,
  ASSIGN_UNITS_TO_ZONE,
} from "@components/smart_zones/graphql";

export default {
  name: "PageSmartUnitAdd",
  components: {
    DeviceGaugeCluster,
    SelectUnitType,
    LayoutPage,
    CardLayout,
    DialogLayout,
    // InputTimeZone, // removed from zone creation
    CardDeviceLocations,
  },
  setup() {
    const route = useRoute();
    const subtype = computed(() => {
      return route.query.subtype;
    });

    const unitNameMinLength = 2;
    const { currentFacilityId, currentFacility } = useFacility();
    const { hasPMS } = usePMS();
    const { searchExistingUnits } = useSmartUnits();

    const {
      healthyFacilityDevices,
      devicesLoading,
      fetchFacilityDevices,
      refetchInventory,
      cleanUpInventoryRefs,
    } = useInventory();

    const {
      device: transferringDevice,
      deviceName,
      fetchDevice,
    } = useDevice({}, currentFacilityId);

    const smartUnit = ref({});
    const selectedDeviceIds = ref([]);
    const deviceFilter = ref("");

    const availableDevices = computed(() => {
      const fetchedDevice = transferringDevice.value?.id
        ? {
            ...transferringDevice.value,
            name: deviceName.value,
            lastMotion: deviceLastMotion(transferringDevice.value),
          }
        : null;

      const facilityDevices = unref(healthyFacilityDevices);

      // Devices already assigned to the selected unit
      const smartUnitDevices = smartUnit.value?.devices?.length
        ? smartUnit.value.devices
        : null;

      const devices = smartUnitDevices
        ? [...smartUnitDevices, ...facilityDevices]
        : fetchedDevice
        ? [
            fetchedDevice,
            ...facilityDevices.filter((d) => d.id !== fetchedDevice.id),
          ]
        : facilityDevices;

      const deviceSearch = stringSearch(deviceFilter.value);
      return deviceFilter.value
        ? devices?.filter((device) => deviceSearch.includedIn(device.name))
        : devices;
    });
    const selectedDevices = ref([]);

    // Pre-select devices already assigned to the selected unit
    watch(smartUnit, (unit) => {
      if (unit?.devices?.length) {
        selectedDeviceIds.value = [
          ...selectedDeviceIds.value,
          ...unit.devices.map((d) => d.id),
        ];
      }
    });

    // Pre-select transferring device
    watch(transferringDevice, (device) => {
      if (device?.id) {
        selectedDeviceIds.value = [...selectedDeviceIds.value, device.id];
      }
    });

    watch(selectedDeviceIds, () => {
      selectedDevices.value = availableDevices.value.filter((device) =>
        selectedDeviceIds.value.includes(device.id)
      );
    });
    onUnmounted(cleanUpInventoryRefs);
    const { addAlert } = useAlerts();
    return {
      currentFacilityId,
      currentFacility,
      hasPMS,
      devicesLoading,
      fetchFacilityDevices,
      refetchInventory,
      fetchDevice,
      searchExistingUnits,
      smartUnit,
      selectedDeviceIds,
      deviceFilter,
      availableDevices,
      unitNameMinLength,
      addAlert,
      subtype,
      selectedDevices,
    };
  },
  data: () => ({
    existingUnits: [],
    search: null,
    unitExistsAlert: false,
    user: null,
    billingAccount: null,
    existingUnitSelected: false,
    focusNameInput: true,
    openRefreshDeviceWarning: false,
    zoneWithRefreshWarning: null,
    redirectFn: null,
    isSubmitting: false,
    submitAttempted: false,
    facilityUnits: [],
    facilityCameras: [],
    unitSearch: "",
    selectedUnitIds: [],
  }),
  computed: {
    allowCreation() {
      if (!this.smartUnit.name) {
        return false;
      }
      if (this.subtype !== "zone" && this.selectedDeviceIds.length < 1) {
        return false;
      }
      if (this.unitExistsAlert) {
        return false;
      }
      if (this.isSubmitting) {
        return false;
      }
      return true;
    },
    renterId() {
      return this.$route.params.renter_id;
    },
    renterLoading() {
      return this.$apollo.queries.user.loading;
    },
    renterName() {
      if (this.renterLoading) return "...";
      return userDisplayName(this.user);
    },
    pageTitle() {
      if (this.subtype === "zone") return ["Smart Zones", "Create Smart Zone"];
      return this.renterId
        ? ["Renters", this.renterName, "Create Smart Unit"]
        : ["Smart Units", "Inventory", "Create Smart Unit"];
    },
    capitalizedSubtype() {
      return titleCase(this.subtype);
    },
    cardTitle() {
      return `Smart ${this.capitalizedSubtype} Details`;
    },
    labelName() {
      if (this.subtype === "zone") return "Zone Name";
      return `${this.capitalizedSubtype} #`;
    },
    existingUnitsLoading() {
      return this.$apollo.queries.organizationDescendants?.loading;
    },
    selectedExistingZoneId() {
      return this.existingUnits[0]?.id ?? "";
    },
    deviceChargeAmount() {
      if (!this.currentFacility.billingProfile?.deviceChargeAmount) {
        return;
      }
      return `${this.currentFacility.billingProfile?.deviceChargeAmount} `;
    },
    facilityUnitsLoading() {
      return this.$apollo.queries.facilityUnits?.loading;
    },
    // Units not yet assigned to any zone (parent is still the facility)
    unmappedUnits() {
      return this.facilityUnits.filter(
        (u) => u.parentId === this.currentFacilityId
      );
    },
    filteredUnmappedUnits() {
      if (!this.unitSearch) return this.unmappedUnits;
      const s = this.unitSearch.toLowerCase();
      return this.unmappedUnits.filter(
        (u) =>
          u.name.toLowerCase().includes(s) ||
          (u.unitType?.name && u.unitType.name.toLowerCase().includes(s))
      );
    },
    cameraDevices() {
      // For zones, use the dedicated camera fetch (video_camera subtype).
      // For units, fall back to filtering availableDevices (no EEN cameras expected).
      if (this.subtype === "zone") {
        return this.facilityCameras;
      }
      return (this.availableDevices || []).filter((d) =>
        this.isCameraDevice(d)
      );
    },
    sensorDevices() {
      return (this.availableDevices || []).filter(
        (d) => !this.isCameraDevice(d)
      );
    },
    // UI-only grouping: zones show Cameras + Sensors as separate tables;
    // everything else shows a single ungrouped table.
    deviceGroups() {
      if (this.subtype === "zone") {
        return [
          { key: "cameras", label: "Cameras", devices: this.cameraDevices },
          { key: "sensors", label: "Sensors", devices: this.sensorDevices },
        ];
      }
      return [{ key: "all", label: null, devices: this.availableDevices }];
    },
  },
  apollo: {
    user: {
      query: findUser,
      variables() {
        return { id: this.$route.params.renter_id };
      },
      skip() {
        // Only run query if there's a renter_id
        return !this.$route.params.renter_id;
      },
    },
    billingAccount: {
      query: userBillingAccounts,
      variables() {
        return {
          userId: this.$route.params.renter_id,
          parentOrganizationId: this.currentFacilityId,
        };
      },
      update({ userBillingAccounts }) {
        return userBillingAccounts.find(
          (account) => account.billingAccount.type == "consumer"
        );
      },
      skip() {
        // Only run query if there's a renter_id
        return !this.renterId;
      },
    },
    facilityUnits: {
      query: GET_FACILITY_UNITS,
      variables() {
        return { facilityId: this.currentFacilityId };
      },
      update({ organizationDescendants }) {
        return organizationDescendants || [];
      },
      skip() {
        // Only needed for the zone Unit Mapping section
        return this.subtype !== "zone" || !this.currentFacilityId;
      },
    },
    facilityCameras: {
      query: GET_FACILITY_CAMERAS,
      variables() {
        return { facilityId: this.currentFacilityId };
      },
      update({ organizationDevices }) {
        return (organizationDevices || []).map((d) => ({
          ...d,
          lastMotion: deviceLastMotion(d),
        }));
      },
      skip() {
        return this.subtype !== "zone" || !this.currentFacilityId;
      },
    },
  },
  watch: {
    selectedDeviceIds(newIds) {
      // Clear inline error once user has selected at least one device
      if (newIds.length > 0) {
        this.submitAttempted = false;
      }
    },
    async search(searchText) {
      // Search is nothing, or items have already been requested
      if (!searchText) {
        this.existingUnits = [];
        return;
      }

      try {
        // Reduce and transform the data
        const units = await this.searchExistingUnits(this.$apollo, searchText);

        // If there's not an exact match, append a "new" unit
        const matchSearchText = stringSearch(searchText);
        const exactMatch = units.find((unit) =>
          matchSearchText.exactMatch(unit.name)
        );

        if (!exactMatch) {
          this.existingUnits = [
            {
              name: searchText.trim(),
              type: "zone",
              assigned: false,
              devices: [],
              unitTypeId: null,
              nameTooShort: searchText.trim().length < this.unitNameMinLength,
            },
            ...units,
          ];
        } else {
          this.existingUnits = units;
        }
      } catch (errorMessage) {
        this.addAlert({
          message: errorMessage,
        });
      }
    },
  },
  created() {
    if (this.hasPMS && this.subtype == "unit") {
      this.$router.replace({ name: "SmartUnits" });
    }
  },
  beforeMount() {
    const smartUnitName = this.$route.query?.name;
    if (smartUnitName) {
      this.smartUnit = {
        name: smartUnitName,
        type: "zone",
        assigned: false,
        devices: [],
        unitTypeId: null,
      };
      this.existingUnits = [this.smartUnit];
    }
    this.fetchFacilityDevices(this.$apollo);

    const deviceId = this.$route.query?.device;
    if (deviceId) {
      this.fetchDevice(this.$apollo, deviceId);
    }
  },
  mounted() {
    if (this.smartUnit.name) {
      if (this.subtype === "unit") {
        this.$refs.unitType.focus();
      } else {
        this.focusTimeZone();
      }
    } else {
      this.$refs.unitName.focus();
    }
  },
  methods: {
    markSubmitAttempted() {
      // Capture click on a disabled Create/Update button to show inline validation
      if (!this.allowCreation) {
        this.submitAttempted = true;
      }
    },
    selectSmartUnit() {
      this.existingUnitSelected = true;
      this.unitExistsAlert = false;
    },
    async onClearClicked() {
      if (!this.existingUnitSelected) return;
      this.existingUnitSelected = false;
      this.selectedDeviceIds = [];
      await this.refetchInventory();
    },

    // Mutation methods
    async addSmartUnit() {
      if (this.isSubmitting) {
        return;
      }

      this.isSubmitting = true;

      try {
        if (this.existingUnitSelected) {
          await this.updateSmartUnit();
          return;
        }

        // Create the new zone/unit with the selected devices.
        // A zone inherits its facility's time zone (no longer chosen in the UI).
        const payload = {
          ...this.smartUnit,
          parent: { id: this.currentFacilityId },
        };
        if (this.subtype === "zone") {
          payload.timeZone = this.currentFacility?.timeZone;
        }
        const zone = await addSmartUnit(
          this.$apollo,
          payload,
          this.selectedDeviceIds,
          this.subtype,
          this.selectedDevices
        );
        // Assign any units selected in the Unit Mapping section to the new zone
        if (
          this.subtype === "zone" &&
          this.selectedUnitIds.length &&
          zone?.id
        ) {
          await this.$apollo.mutate({
            mutation: ASSIGN_UNITS_TO_ZONE,
            variables: {
              zoneId: zone.id,
              unitIds: this.selectedUnitIds,
            },
          });
        }

        if (this.subtype === "zone") {
          this.$router.go(-1);
        } else if (zone && this.user) {
          if (this.warnOnRefresh(zone)) {
            this.openRefreshDeviceWarning = true;
            this.zoneWithRefreshWarning = zone;
            this.redirectFn = () => {
              this.$router.go(-1);
            };
          } else {
            this.doAssignZoneToRenter(zone, () => {
              this.$router.go(-1);
            });
          }
        } else {
          this.$router.push({
            name: "Inventory",
          });
        }
      } catch (error) {
        this.addAlert({
          message: `There was an error adding the smart ${this.subtype}`,
        });
        this.isSubmitting = false;
      }
    },

    async updateSmartUnit() {
      if (!this.existingUnitSelected) return;

      // Split the IDs into buckets for remove/ignore
      const deviceIds = this.smartUnit.devices.reduce(
        (acc, device) => {
          if (!this.selectedDeviceIds.includes(device.id)) {
            acc.removedIds.push(device.id);
          } else {
            acc.ignoreIds.push(device.id);
          }
          return acc;
        },
        { removedIds: [], ignoreIds: [] }
      );

      // Get device IDs to add to the selected unit, ignoring already assigned
      const addIds = this.selectedDeviceIds.filter(
        (id) => !deviceIds.ignoreIds.includes(id)
      );

      try {
        if (deviceIds.removedIds.length > 0) {
          await this.$apollo.mutate({
            // "add" it back to facility inventory
            mutation: addOrganizationDevices,
            variables: {
              devices: deviceIds.removedIds,
              id: this.currentFacilityId,
            },
          });
        }

        if (addIds.length > 0) {
          await this.$apollo.mutate({
            // Add new devices to smart unit
            mutation: addOrganizationDevices,
            variables: {
              devices: addIds,
              id: this.smartUnit.id,
            },
          });
        }
        if (this.subtype === "zone") {
          this.$router.go(-1);
        } else if (this.user) {
          // Interactor needs this for refetchQueries
          this.smartUnit.parentId = this.currentFacilityId;

          if (this.warnOnRefresh(this.smartUnit)) {
            this.openRefreshDeviceWarning = true;
            this.zoneWithRefreshWarning = this.smartUnit;
            this.redirectFn = () => {
              this.gotoRenter(this.user);
            };
          } else {
            this.doAssignZoneToRenter(this.smartUnit, () => {
              //Go to assigned smart unit drilldown here.
              this.gotoRenter(this.user);
            });
          }
        } else {
          this.gotoSmartUnit(this.smartUnit);
        }
      } catch (error) {
        this.addAlert({
          message: `There was an error adding the smart ${this.subtype}`,
        });
      }
    },
    async assignSmartUnit(zone) {
      try {
        if (zone && this.user) {
          // Interactor needs this for refetchQueries
          zone.parentId = this.currentFacilityId;

          // Add the zone/unit to the renter and make them a responder
          await assignZoneToRenter(
            this.$apollo,
            zone,
            this.user,
            this.billingAccount
          );

          // let's go back to the renter
          this.$router.go(-1);
        } else {
          this.addAlert({
            message: "Something went wrong assigning the smart unit",
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    async doAssignZoneToRenter(zone, redirectFn) {
      // Add the zone/unit to the renter and make them a responder
      await assignZoneToRenter(
        this.$apollo,
        zone,
        this.user,
        this.billingAccount
      );
      redirectFn();
    },

    warnOnRefresh(zone) {
      return !!zone.devices.find((d) => d.labels?.isRefresh);
    },

    cancel() {
      this.selectedDeviceIds = [];
      this.smartUnit.name = "";
      this.smartUnit.unitTypeId = null;

      this.$router.go(-1);
    },

    // Name Collision Option methods
    gotoSmartUnit({ id: zoneId }) {
      this.$router.push({
        name: "ViewSmartUnit",
        params: {
          zone_id: zoneId,
        },
      });
    },

    gotoRenter({ id: renterId }) {
      this.$router.push({
        name: "ViewRenter",
        params: {
          renter_id: renterId,
        },
      });
    },

    useDifferentName() {
      this.smartUnit = {};
      this.unitExistsAlert = false;
      this.$nextTick(() => this.$refs.unitName.focus());
    },

    unitSelected(unit) {
      if (!unit) {
        this.unitExistsAlert = false;
        this.smartUnit = {};
      } else if (unit.id) {
        this.unitExistsAlert = true;
        this.$refs.unitName.blur();
      } else {
        // The "new" unit is selected
        this.unitExistsAlert = false;
        if (this.subtype === "unit") {
          this.$refs.unitType.focus();
        }
      }
    },
    // Helper methods
    assignedDevices({ devices }) {
      if (devices && devices.length > 0) {
        return devices
          .map((device) => device.shortId || device.externalId)
          .sort()
          .join(" | ");
      }
    },
    focusTimeZone() {
      // The time-zone field is no longer rendered for zones; guard the ref.
      if (this.$refs.timeZone) this.$refs.timeZone.focusInput();
    },
    // "Climate Control · 10×10" — unit type name + dimensions
    unitDetails(unit) {
      const parts = [];
      if (unit.unitType?.name) parts.push(unit.unitType.name);
      if (unit.unitType?.width && unit.unitType?.length) {
        parts.push(`${unit.unitType.width}×${unit.unitType.length}`);
      }
      return parts.join(" · ");
    },
    // shortIds of the unit's smart (non-camera) devices — a unit can have several
    smartDeviceIds(unit) {
      return (unit.devices || [])
        .filter((d) => d.subtype !== "video_camera")
        .map((d) => d.shortId)
        .filter(Boolean);
    },
    isCameraDevice(device) {
      return device.type === "een" || device.subtype === "video_camera";
    },
    deviceType,
  },
};
</script>

<style scoped>
/* Align each unmapped-unit row as a consistent grid: checkbox | fixed-width
   unit name | flexible details | right-aligned device column, vertically centered. */
.unit-row {
  align-items: center;
}
.unit-name {
  flex: 0 0 64px;
  width: 64px;
}
.unit-details {
  flex: 1 1 auto;
  min-width: 0;
}
.device-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-left: 12px;
}
</style>
